/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { LearnerType, LoadStatus, Edits } from "../../../types";

export interface UseWithLearnerTypes {
  learnerTypes: Record<string, LearnerType>;
  learnerTypeEdits: Record<string, Edits<LearnerType>>;
  learnerTypeLoadStatus: LoadStatus;
  loadAllLearnerTypes: () => Promise<LearnerType[]>;
  createLearnerType: (newData: Partial<LearnerType>) => Promise<LearnerType>;
  deleteLearnerType: (learnerType: LearnerType) => Promise<void>;
  updateLearnerType: (newData: Partial<LearnerType>) => Promise<LearnerType>;
}

export default function useWithLearnerTypes(): UseWithLearnerTypes {
  const { learnerTypes, learnerTypeEdits, learnerTypeLoadStatus } =
    useAppSelector((state) => state.learnerTypes);
  const dispatch = useAppDispatch();

  async function loadAllLearnerTypes(): Promise<LearnerType[]> {
    if (learnerTypeLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadLearnerTypes()).unwrap();
  }

  async function createLearnerType(
    data: Partial<LearnerType>
  ): Promise<LearnerType> {
    if (!data.name) throw new Error("name required");
    return await dispatch(
      actions.addOrUpdateLearnerType({
        id: data.id || "",
        name: data.name || "",
        desc: data.desc || "",
        characteristics: data.characteristics || [],
      })
    ).unwrap();
  }

  async function deleteLearnerType(learnerType: LearnerType): Promise<void> {
    await dispatch(actions.deleteLearnerType(learnerType.id));
  }

  async function updateLearnerType(
    data: Partial<LearnerType>
  ): Promise<LearnerType> {
    return await dispatch(actions.addOrUpdateLearnerType(data)).unwrap();
  }

  return {
    learnerTypes,
    learnerTypeEdits,
    learnerTypeLoadStatus,
    loadAllLearnerTypes,
    createLearnerType,
    deleteLearnerType,
    updateLearnerType,
  };
}
